/**
 * Single design-board media read path: signed callable, then canonical Storage getBlob.
 */

import { fetchDesignBoardFileReadUrl } from "./designBoardFileReadUrlCache";
import { enrichProductClientPortalFileMetaFromCanonical } from "./projectProductClientPortalFileMetaModel";
import { resolvePmProjectFileReadStoragePath } from "./projectDesignBoardFileReadPathModel";
import {
  fetchAuthorizedProjectFileBlob,
  revokeTemporaryObjectUrl,
  STORAGE_BLOB_FETCH_TIMEOUT_MS,
  STORAGE_ORIGINAL_BLOB_FETCH_TIMEOUT_MS,
} from "./projectFileAuthenticatedBlob";
import {
  openProjectFileViaAuthenticatedBlob,
  projectFileAllowsLegacyTokenizedUrlCompat,
  resolveProjectFileSecureObjectPath,
} from "./projectFileSecureStorageModel";
import { designBoardMediaPreviewKind } from "./projectDesignBoardMediaModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

const negativeAttemptByKey = new Map();
const MAX_NEGATIVE_ATTEMPTS = 2;

export function designBoardFileReadFingerprint(file = {}, { projectId = "", fileId = "", objectKind = "original" } = {}) {
  const pid = safeStr(projectId || file?.projectId);
  const fid = safeStr(fileId || file?.id || file?.fileId);
  const kind = safeStr(objectKind) === "thumbnail" ? "thumbnail" : "original";
  const enriched = enrichDesignBoardFileRecord(file, { projectId: pid, fileId: fid });
  const path = resolvePmProjectFileReadStoragePath(enriched, { projectId: pid, fileId: fid, objectKind: kind });
  return [pid, fid, kind, path, safeStr(enriched.storageSchemaVersion), safeStr(enriched.url)].join("|");
}

export function enrichDesignBoardFileRecord(file = {}, { projectId = "", fileId = "" } = {}) {
  const fid = safeStr(fileId || file?.id || file?.fileId);
  const pid = safeStr(projectId || file?.projectId);
  return enrichProductClientPortalFileMetaFromCanonical(
    { ...file, id: fid, fileId: fid, projectId: pid },
    file
  );
}

function negativeKey(fingerprint) {
  return `neg:${fingerprint}`;
}

export function clearDesignBoardProjectFileReadResolverState() {
  negativeAttemptByKey.clear();
}

export async function resolveDesignBoardProjectFileOpenUrl(
  file = {},
  {
    designBoardProjectId = "",
    objectKind = "original",
    thumbnailFallbackToOriginal = false,
    storage,
    audience = "portal",
    timeoutMs,
    getReadUrl,
    getBlob,
    ref,
    createObjectURL,
  } = {}
) {
  const pid = safeStr(designBoardProjectId);
  const enriched = enrichDesignBoardFileRecord(file, { projectId: pid });
  const fileId = safeStr(enriched.id || enriched.fileId);
  if (!pid || !fileId) {
    return { ok: false, errorCode: "invalid_args", fileId, message: "Invalid file reference." };
  }

  const kind = safeStr(objectKind) === "thumbnail" ? "thumbnail" : "original";
  const fingerprint = designBoardFileReadFingerprint(enriched, { projectId: pid, fileId, objectKind: kind });
  const negKey = negativeKey(fingerprint);
  const negCount = negativeAttemptByKey.get(negKey) || 0;
  if (negCount >= MAX_NEGATIVE_ATTEMPTS) {
    return { ok: false, errorCode: "permission_denied", fileId, message: "Could not load this file." };
  }

  const storedLegacy = safeStr(enriched.url);
  if (projectFileAllowsLegacyTokenizedUrlCompat(enriched) && storedLegacy) {
    return { ok: true, url: storedLegacy, method: "stored_legacy_compat", fileId };
  }

  let signed = await fetchDesignBoardFileReadUrl(
    { projectId: pid, fileId, objectKind: kind, pathFingerprint: fingerprint },
    { getReadUrl }
  );
  if (
    !signed.ok
    && kind === "thumbnail"
    && thumbnailFallbackToOriginal
    && (signed.errorCode === "object_not_found" || signed.errorCode === "read_url_failed")
  ) {
    const originalFingerprint = designBoardFileReadFingerprint(enriched, {
      projectId: pid,
      fileId,
      objectKind: "original",
    });
    signed = await fetchDesignBoardFileReadUrl(
      { projectId: pid, fileId, objectKind: "original", pathFingerprint: originalFingerprint },
      { getReadUrl }
    );
  }
  if (signed.ok && signed.readUrl) {
    negativeAttemptByKey.delete(negKey);
    return { ok: true, url: signed.readUrl, method: "design_board_signed_read", fileId };
  }

  const blobResult = await fetchDesignBoardCanonicalBlob(enriched, {
    storage,
    objectKind: kind,
    thumbnailFallbackToOriginal,
    timeoutMs,
    getBlob,
    ref,
    createObjectURL,
  });
  if (blobResult.ok) {
    negativeAttemptByKey.delete(negKey);
    return {
      ok: true,
      url: blobResult.objectUrl,
      method: "authenticated_blob",
      fileId,
      revoke: blobResult.revoke,
    };
  }

  negativeAttemptByKey.set(negKey, negCount + 1);
  const errorCode = signed.errorCode === "read_url_failed" ? "permission_denied" : blobResult.errorCode || "open_failed";
  return { ok: false, errorCode, fileId, message: blobResult.message || signed.error || "Could not load this file." };
}

async function fetchDesignBoardCanonicalBlob(
  file,
  { storage, objectKind = "original", thumbnailFallbackToOriginal = false, timeoutMs, getBlob, ref, createObjectURL } = {}
) {
  const kind = safeStr(objectKind) === "thumbnail" ? "thumbnail" : "original";
  const path = resolveProjectFileSecureObjectPath(file, { kind });
  if (path) {
    const timeout = kind === "thumbnail" ? STORAGE_BLOB_FETCH_TIMEOUT_MS : timeoutMs || STORAGE_ORIGINAL_BLOB_FETCH_TIMEOUT_MS;
    let result = await fetchAuthorizedProjectFileBlob({
      storage,
      path,
      timeoutMs: timeout,
      getBlob,
      ref,
      createObjectURL,
    });
    if (result?.ok) {
      return {
        ok: true,
        objectUrl: result.objectUrl,
        revoke: () => revokeTemporaryObjectUrl(result.objectUrl),
      };
    }
    if (kind === "thumbnail" && thumbnailFallbackToOriginal) {
      const originalPath = resolveProjectFileSecureObjectPath(file, { kind: "original" });
      if (originalPath && originalPath !== path) {
        result = await fetchAuthorizedProjectFileBlob({
          storage,
          path: originalPath,
          timeoutMs: STORAGE_ORIGINAL_BLOB_FETCH_TIMEOUT_MS,
          getBlob,
          ref,
          createObjectURL,
        });
      }
      if (result?.ok) {
        return {
          ok: true,
          objectUrl: result.objectUrl,
          revoke: () => revokeTemporaryObjectUrl(result.objectUrl),
        };
      }
    }
    return { ok: false, errorCode: result?.errorCode || "authorized_blob_failed" };
  }

  const fallback = await openProjectFileViaAuthenticatedBlob(file, {
    storage,
    timeoutMs,
    getBlob,
    ref,
    createObjectURL,
  });
  if (fallback.ok) {
    return {
      ok: true,
      objectUrl: fallback.objectUrl,
      revoke: fallback.revoke,
    };
  }
  return { ok: false, errorCode: fallback.errorCode || "missing_secure_path" };
}

/** Picker/board probe: image tries thumbnail then original via the same resolver. */
export async function probeDesignBoardFileReadableForPicker(
  { projectId = "", file = {} } = {},
  deps = {}
) {
  const enriched = enrichDesignBoardFileRecord(file, { projectId });
  const fileId = safeStr(enriched.id || enriched.fileId);
  const pid = safeStr(projectId || enriched.projectId);
  if (!pid || !fileId) {
    return { ok: false, status: "invalid", error: "Missing file reference." };
  }

  const mediaKind = designBoardMediaPreviewKind(enriched);
  const objectKind = mediaKind === "image" ? "thumbnail" : "original";
  const open = await resolveDesignBoardProjectFileOpenUrl(enriched, {
    designBoardProjectId: pid,
    objectKind,
    thumbnailFallbackToOriginal: mediaKind === "image",
    ...deps,
  });
  if (open.ok && open.url) {
    return { ok: true, status: "readable", readUrl: open.url, method: open.method };
  }
  const errText = safeStr(open.message).toLowerCase();
  const status =
    errText.includes("not available") || errText.includes("not found") || open.errorCode === "object_not_found"
      ? "missing_object"
      : "unavailable";
  return { ok: false, status, error: open.message || "Could not read this file." };
}
